import * as planningApi from "app/api/planning"
import * as usersApi from "app/api/users"
import User from "app/models/user"
import moment from "moment"
import { SagaIterator } from "redux-saga"
import { call, put, takeLatest } from "redux-saga/effects"
import { handleError } from "app/redux/errorHandler/errorHandler.saga"
import HrUnavailability from "app/models/hrUnavailability"
import HrGeneralLeave from "app/models/hrGeneralLeave"
import { Statuses } from "app/models/request"
import { UserMonth } from "./absencesTable.reducer"
import * as actions from "./absencesTable.actions"

export function* getAbsencesSaga(action: actions.GetAbsencesAction): SagaIterator {
  try {
    const { date, params, userParams } = action.payload
    const usersResponse = yield call(usersApi.getUsers, userParams)
    const users: User[] = usersResponse && ((usersResponse!.data as unknown) as User[])
    const hrUnavailabilitiesResponse = yield call(planningApi.getHrUnavailabilities, {
      start_time: params.from,
      end_time: params.to,
      statuses: [Statuses.Approved],
    })
    const hrGeneralLeavesResponse = yield call(planningApi.getHrGeneralLeaves, params)
    const hrUnavailabilities: HrUnavailability[] = hrUnavailabilitiesResponse?.data || []
    const hrGeneralLeaves: HrGeneralLeave[] = hrGeneralLeavesResponse?.data || []
    const usersWithHrUnavailabilitiesAndHrGeneralLeaves = mapUsersWithHrUnavailabilitiesAndGeneralLeaves({
      users,
      hrGeneralLeaves,
      hrUnavailabilities,
      date: params.from,
    })
    yield put(actions.getAbsencesSuccess(usersWithHrUnavailabilitiesAndHrGeneralLeaves, date!))
  } catch (e) {
    yield put(actions.getAbsencesFail(e))
    yield call(handleError, { error: e })
  }
}

function mapUsersWithHrUnavailabilitiesAndGeneralLeaves({
  users,
  hrUnavailabilities,
  hrGeneralLeaves,
  date,
}: {
  users: User[]
  hrUnavailabilities: HrUnavailability[]
  hrGeneralLeaves: HrGeneralLeave[]
  date: string
}) {
  const utc = moment.utc(date)
  const year = Number(utc.format("Y"))
  const month = Number(utc.format("M"))
  const daysInMonth = moment.utc(date).daysInMonth()
  const monthDays = Array.from({ length: daysInMonth })
  return users.reduce((result: UserMonth[], user: User) => {
    const absences = monthDays.map((_: any, index: number) => {
      const currentDate = moment.utc([year, month - 1, index + 1]).format()
      const hrUnavailabilitiesByDateAndUserId = getHrUnavailabilitiesByDateAndUserId(
        hrUnavailabilities,
        currentDate,
        user.id,
      )
      const hrGeneralLeavesByDate = getHrGeneralLeavesByDate(hrGeneralLeaves, currentDate)
      return {
        hrUnavailabilities: hrUnavailabilitiesByDateAndUserId,
        hrGeneralLeaves: hrGeneralLeavesByDate,
        date: currentDate,
        selected: false,
      }
    })
    return [...result, { user, absences }]
  }, [])
}

function getHrUnavailabilitiesByDateAndUserId(
  hrUnavailabilities: HrUnavailability[],
  date: string,
  userId?: string,
): HrUnavailability[] {
  return hrUnavailabilities.filter(
    (hru: HrUnavailability) =>
      (!userId || userId === hru.employee_id || hru.employee_id === "") && moment.utc(hru.date).isSame(date),
  )
}

function getHrGeneralLeavesByDate(hrGeneralLeaves: HrGeneralLeave[], date: string): HrGeneralLeave[] {
  return hrGeneralLeaves.filter((hrgl: HrGeneralLeave) => moment(hrgl.date).isSame(date, "date"))
}

export default function* absencesTableSaga(): SagaIterator {
  yield takeLatest(actions.Types.GET, getAbsencesSaga)
}
